import { JSX } from 'react';
import { Controller, UseFormReturn } from 'react-hook-form';

import { ErrorMessage } from '@hookform/error-message';

import { Label } from 'components/Label';
import { Select } from 'components/Select';
import { Flex, Grid } from 'components/layout';
import { Typography } from 'components/typography';
import {
  EQUIPMENT_PLACEHOLDER,
  EQUIPMENT_REQUIRED_MESSAGE,
  validateEquipmentRequired,
} from 'pages/QuoteView/LoadBuilding/utils/suggestedEquipment';
import { NormalizedLoad } from 'types/Load';
import { Undef } from 'types/UtilityTypes';
import { validateRequiredSelectMembership } from 'utils/formValidation';

import { LoadBuildingTextInput } from '../components/LoadBuildingTextInput';
import {
  equipmentSizeList,
  serviceTypes,
  transportTypes,
  unitsList,
  weightUnitsList,
} from './constants';

export function SpecificationsForm({
  formMethods,
  isCreateMode,
}: {
  formMethods: UseFormReturn<NormalizedLoad>;
  isCreateMode: boolean;
}): JSX.Element {
  const {
    control,
    formState: { errors },
    watch,
  } = formMethods;

  const modes = ['Truckload'];
  const watchedGrossWeight = watch('specifications.totalWeight.val');

  return (
    <Grid cols='4' gap='sm' className='mx-0 w-full'>
      <div className='col-span-4'>
        <Label name={'mode'} required={true}>
          Mode
        </Label>
        <Controller
          name='mode'
          control={control}
          rules={{ required: 'Required' }}
          render={({ field }) => (
            <Select
              showSearch
              name={field.name}
              disabled={true} // Only Truckloads supported
              className='h-8'
              placeholder={'Choose'}
              optionFilterProp='children'
              onChange={field.onChange}
              value={field.value}
              options={modes?.map((mode) => ({
                value: mode,
                label: mode,
              }))}
            />
          )}
        />
        <ErrorMessage
          errors={errors}
          name={'mode'}
          render={({ message }: { message: string }) => (
            <Typography variant='body-xs' className='text-error-500'>
              {message}
            </Typography>
          )}
        />
      </div>

      <div className='col-span-4'>
        <Label name={'serviceType'} required={true}>
          Service Type
        </Label>
        <Controller
          name='specifications.serviceType'
          control={control}
          rules={{ required: 'Required' }}
          render={({ field }) => (
            <Select
              showSearch
              name={field.name}
              className='h-8'
              placeholder={'Choose'}
              optionFilterProp='children'
              onChange={field.onChange}
              value={field.value}
              options={serviceTypes?.map((serviceType) => ({
                value: serviceType,
                label: serviceType,
              }))}
            />
          )}
        />
        <ErrorMessage
          errors={errors}
          name={'specifications.serviceType'}
          render={({ message }: { message: string }) => (
            <Typography variant='body-xs' className='text-error-500'>
              {message}
            </Typography>
          )}
        />
      </div>

      <Grid cols='4' gap='sm' className='mx-0 w-full col-span-4'>
        <div className='col-span-2'>
          <LoadBuildingTextInput
            name='specifications.commodities'
            label='Item'
            required={true}
          />
        </div>

        <div className='col-span-1'>
          <LoadBuildingTextInput
            name='specifications.totalPieces.val'
            label='Quantity'
            inputType='number'
            options={{ valueAsNumber: true }}
            required={false}
          />
        </div>

        <div className='col-span-1'>
          <Label name={'specifications.totalPieces.unit'} required={false}>
            Units
          </Label>
          <Controller
            name='specifications.totalPieces.unit'
            control={control}
            rules={{ required: false }}
            render={({ field }) => (
              <Select
                showSearch
                name={field.name}
                className='h-8'
                placeholder={'Choose'}
                optionFilterProp='children'
                onChange={field.onChange}
                value={field.value}
                options={unitsList?.map((unit) => ({
                  value: unit,
                  label: unit,
                }))}
              />
            )}
          />
        </div>
      </Grid>

      <Grid cols='3' gap='sm' className='mx-0 w-full col-span-4'>
        <div className='col-span-2'>
          <Label name={'specifications.transportType'} required={true}>
            Equipment
          </Label>
          <Controller
            name='specifications.transportType'
            control={control}
            rules={{
              validate: (value) => {
                if (!isCreateMode) {
                  return validateEquipmentRequired(value);
                }
                return validateRequiredSelectMembership(
                  value,
                  (transportTypes ?? []).map((type) => String(type)),
                  { message: EQUIPMENT_REQUIRED_MESSAGE }
                );
              },
            }}
            render={({ field }) => {
              const optionValues = new Set(
                (transportTypes ?? []).map((type) => String(type))
              );
              const selected = String(field.value ?? '').trim();
              let selectValue: Undef<string>;
              if (selected) {
                if (!isCreateMode || optionValues.has(selected)) {
                  selectValue = selected;
                }
              }
              return (
                <Select
                  showSearch
                  name={field.name}
                  className='h-8'
                  placeholder={EQUIPMENT_PLACEHOLDER}
                  optionFilterProp='children'
                  onChange={field.onChange}
                  value={selectValue}
                  options={transportTypes?.map((type) => ({
                    value: type,
                    label: type,
                  }))}
                />
              );
            }}
          />
          <ErrorMessage
            errors={errors}
            name={'specifications.transportType'}
            render={({ message }: { message: string }) => (
              <Typography variant='body-xs' className='text-error-500'>
                {message}
              </Typography>
            )}
          />
        </div>

        <div className='col-span-1'>
          <Label name={'specifications.transportSize'} required={false}>
            Size
          </Label>
          <Controller
            name='specifications.transportSize'
            control={control}
            rules={{ required: false }}
            render={({ field }) => (
              <Select
                showSearch
                name={field.name}
                className='h-8'
                placeholder={'Choose'}
                optionFilterProp='children'
                onChange={field.onChange}
                value={field.value}
                options={equipmentSizeList?.map((size) => ({
                  value: size,
                  label: size,
                }))}
              />
            )}
          />
        </div>
      </Grid>

      <Grid cols='2' gap='sm' className='mx-0 w-full col-span-4'>
        <Grid cols='3' gap='sm' className='mx-0 w-full col-span-2'>
          <div className='col-span-2'>
            <LoadBuildingTextInput
              name='specifications.totalWeight.val'
              label='Gross Weight'
              inputType='number'
              options={{ valueAsNumber: true }}
              required={false}
            />
          </div>

          <Flex direction='col' className='col-span-1'>
            <Label
              name={'specifications.totalWeight.unit'}
              required={watchedGrossWeight > 0}
              aiIconOnly={true}
            >
              Units
            </Label>
            <Controller
              name='specifications.totalWeight.unit'
              control={control}
              rules={{ required: watchedGrossWeight > 0 }}
              render={({ field }) => (
                <Select
                  showSearch
                  name={field.name}
                  className='h-8'
                  placeholder={'Choose'}
                  optionFilterProp='children'
                  onChange={field.onChange}
                  value={field.value}
                  options={weightUnitsList?.map((unit) => ({
                    value: unit,
                    label: unit,
                  }))}
                />
              )}
            />
          </Flex>
        </Grid>
      </Grid>
    </Grid>
  );
}
